import Vuex from 'vuex';
import Vue from 'vue';

Vue.use(Vuex);

import fieldStore from './stores/FieldStore';
import { irrigationSystemTypes, pipeSizes } from './constants';
import { getDailyForecast } from './services/WeatherService';

// Monotonic token to discard stale weather responses. If a user selects
// field A then quickly field B, only the latest request may write state —
// otherwise a slow A response could overwrite B's forecast.
let weatherRequestId = 0;

export default new Vuex.Store({
  state: {
    user: null,
    fields: [],
    activeField: null,
    activePolygon: null,
    activeMarker: null,
    mapPolygons: [],
    allMarkers: [],
    editMode: false,
    draftCrop: null,
    draftIrrigationSystem: null,
    draftPipeSize: null,
    fetchingResults: false,
    result: null,
    draftField: null,
    userGuideMode: false,
    shouldIgnoreResult: false,
    irrigationWindow: '',
    loginWithMobileClicked: false,
    weather: null,
    weatherLoading: false,
    weatherError: null,
  },
  mutations: {
    setUser(state, user) {
      state.user = user;
    },
    setFields(state, fields) {
      state.fields = fields || [];
    },
    setActiveField(state, field) {
      state.activeField = field;
    },
    setDraftField(state, field) {
      state.draftField = field;
    },
    setActivePolygon(state, polygon) {
      state.activePolygon = polygon;
    },

    setFetchingResults(state, fetching) {
      state.fetchingResults = fetching;
    },

    setUserGuideMode(state, status) {
      state.userGuideMode = status;
    },

    setResults(state, result) {
      state.result = result;
    },

    setLoginWithMobile(state, status) {
      state.loginWithMobileClicked = status;
    },

    setActiveMarker(state, marker) {
      state.activeMarker = marker;
    },

    setShouldIgnoreResult(state, status) {
      state.shouldIgnoreResult = status;
    },

    setWeather(state, weather) {
      state.weather = weather;
    },
    setWeatherLoading(state, loading) {
      state.weatherLoading = loading;
    },
    setWeatherError(state, error) {
      state.weatherError = error;
    },

    setMapPolygons(state, polygons) {
      state.mapPolygons = polygons;
    },

    setDraftCrop(state, crop) {
      state.draftCrop = crop;
    },
    setDraftIrrigationSystem(state, irrigationSystem) {
      state.draftIrrigationSystem = irrigationSystem;
    },
    setDraftPipeSize(state, pipeSize) {
      state.draftPipeSize = pipeSize;
    },

    setIrrigationResult(state, result) {
      state.irrigationWindow = result;
    },

    setEditMode(state, edit) {
      state.editMode = edit;
    },

    setMapMarkers(state, marker) {
      state.allMarkers.push(marker);
    },

    setAllMarkers(state, markers) {
      state.allMarkers = markers;
    },

    addMapPolygon(state, polygon) {
      state.mapPolygons.push(polygon);
    },

    addField(state, field) {
      if (!Array.isArray(state.fields)) {
        state.fields = [];
      }
      state.fields.push(field);
    },

    removeField(state, fieldId) {
      state.fields = state.fields.filter(field => field.key !== fieldId);
    },

    clearMapPolygons(state) {
      state.mapPolygons = [];
    },

    removeMapMarker(state, marker) {
      const index = state.allMarkers.indexOf(marker);
      if (index !== -1) {
        state.allMarkers.splice(index, 1);
      }
    },

    removeMapPolygon(state, polygon) {
      const index = state.mapPolygons.indexOf(polygon);
      if (index !== -1) {
        state.mapPolygons.splice(index, 1);
      }
    }
  },

  getters: {
    getIrrigationRatio: (state) => {
      // A selected saved field's own irrigation system wins; the draft value
      // only applies while drawing a new field (no activeField). Otherwise a
      // stale draftIrrigationSystem from a previous draw would be used for a
      // clicked saved field.
      const system = (state.activeField && state.activeField.irrigationSystem) || state.draftIrrigationSystem;
      const idx = irrigationSystemTypes.indexOf(system);
      switch(idx) {
        case 0:
          return 0.85;
        case 1:
          return 0.8;
        case 2:
          return 0.8;
        case 3:
          return 0.75;
        case 4:
          return 0.85;
        case 5:
          return 0.8;
        case 6:
          return 0.75;
        case 7:
          return 0.75;
        case 8:
          return 0.7;
        case 9:
          return 0.6;
        case 10:
          return 0.5;
        default:
          return 0.85;
      }
    },

    getPipeSizeRatio: (state) => {
      // A selected saved field's own pipe size wins; the draft value only
      // applies while drawing a new field (no activeField). Otherwise a stale
      // draftPipeSize from a previous draw would be used for a clicked saved
      // field, making the duration tab disagree with the info window.
      const size = (state.activeField && state.activeField.pipeSize) || state.draftPipeSize;
      const idx = pipeSizes.indexOf(size);
      switch(idx) {
        case 0:
          return 2.6;
        case 1:
          return 4.0;
        case 2:
          return 6.0;
        case 3:
          return 11.0;
        case 4:
          return 17.0;
        case 5:
          return 24.0;
        case 6:
          return 42.0;
        case 7:
          return 66.0;
        case 8:
          return 90.0;
        default:
          return 2.6;
      }
    },
  },
  actions: {
    async fetchFieldsByDevice({ commit }, email) {
      const fields = await fieldStore.fetchFieldsByDevice(email);
      commit('setFields', fields);
    },

    async deleteField({ commit }, fieldKey) {
      console.log(commit)
      await fieldStore.deleteField(fieldKey);
    },

    logout({ commit }) {
      commit('setUser', null);
      commit('setFields', []);
      commit('setMapPolygons', []);
      commit('setAllMarkers', []);
    },

    clearDraftSelection({commit}) {
      commit('setDraftCrop', null);
      commit('setDraftIrrigationSystem', null);
      commit('setDraftPipeSize', null);
    },

    async fetchWeather({ commit }, { lat, lng }) {
      // Weather is supplementary; a failure here must never block irrigation
      // results. We surface an error string for the UI but swallow the throw.
      const reqId = ++weatherRequestId;
      commit('setWeatherLoading', true);
      commit('setWeatherError', null);
      try {
        const forecast = await getDailyForecast(lat, lng);
        if (reqId !== weatherRequestId) return; // a newer request superseded us
        commit('setWeather', forecast);
      } catch (err) {
        if (reqId !== weatherRequestId) return;
        commit('setWeather', null);
        commit('setWeatherError', err.message || 'weather_unavailable');
      } finally {
        // Only the latest request may clear the spinner, so an early-resolving
        // stale request can't hide an in-flight newer one.
        if (reqId === weatherRequestId) {
          commit('setWeatherLoading', false);
        }
      }
    },

    async login({ commit }, user) {
      commit('setUser', user);
      console.log(user);
      const fields = await fieldStore.fetchFieldsByDevice(user.email || user.phoneNumber);
      // setTimeout(() => {
      //   commit('setFields', fields);
      // }, 2000); Adjust the delay as needed

      commit('setFields', fields);
    },
  },
});
